//
// (c) 2016-2020 Mental Canvas Inc.
//
import {
    _Math,
    EasingFunctions,
    EasingType,
    RBT
} from './utils';
import {
    Camera,
    Material,
    Quaternion,
    Vector3
} from 'three';
import { Bookmark } from './MCBookmark';

interface Dictionary<T> {
    [Key: string]: T;
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Old-school transition code.

export class Transition {
    t : number = 0;
    duration: number = 0;
    pos0: Vector3;
    quat0: Quaternion;
    pos1: Vector3;
    quat1: Quaternion;
    active(): boolean {
        return this.duration > 0.0 && this.t < 1.0;
    }
    setOrigin(camera: Camera) {
        this.pos0 = camera.position.clone();
        this.quat0 = camera.quaternion.clone();
        this.duration = 0.0;
    }
    setDest(camera: Camera) {
        this.pos1 = camera.position.clone();
        this.quat1 = camera.quaternion.clone();
        this.duration = 0.0;
    }
    // Transition data is now read to go - start/end pos/quat both set.
    // Check whether this is a null transition or not, and pick a duration and enable the transition if it's
    // non-null.
    start() {
        var dist = this.pos1.distanceTo(this.pos0);
        var dist_angle = this.quat1.dot(this.quat0);
        dist_angle = 1 - dist_angle * dist_angle;
        //dist_angle is equal to (1-cos theta)/2 - so 0=same orientation, 1=opposite orientation.
        if (dist < 0.001 && dist_angle < 0.001) {
            this.duration = 0.0;
        } else {
            this.duration = 1.0;
        }
        this.t = 0.0;
    }
    advance(deltat: number, camera: Camera) {
        this.t += deltat / this.duration;
        this.t = Math.min(1.0, this.t);
        var t_easing = EasingFunctions.easeInOutQuad(this.t);
        Quaternion.slerp(this.quat0, this.quat1, camera.quaternion, t_easing);
        camera.position.lerpVectors(this.pos0, this.pos1, t_easing);
    }
};

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// New-school: port of the C++ CameraAnimator class.

enum AnimationStateEnum {
    AS_COMPLETED,
    AS_ANIMATING,
    AS_PAUSED
};

///////////////////////// CameraAnimationKeyframe /////////////////////////////////////////

class CameraAnimationKeyframe {
    private m_RBT: RBT;
    private m_StartElapsedTime: number;
    private m_LingerDuration: number;
    private m_TransitionDuration: number;
    private m_TileOpacities: Dictionary<number>;
    constructor(rotation: Quaternion, translation: Vector3, startElapsedTime: number, lingerDuration: number, transitionDuration: number, tileOpacities: Dictionary<number>)
    {
        this.m_RBT = new RBT(rotation, translation);
        this.m_StartElapsedTime = startElapsedTime;
        this.m_LingerDuration = lingerDuration;
        this.m_TransitionDuration = transitionDuration;
        this.m_TileOpacities = tileOpacities;
    }
    dispose() {
    }
    GetRBT(): RBT {
        return this.m_RBT;
    }
    GetStartElapsedTime(): number {
        return this.m_StartElapsedTime;
    }
    GetLingerDuration(getPassThroughAsNegative:boolean=false): number {
        return (getPassThroughAsNegative) ? this.m_LingerDuration : Math.max(0, this.m_LingerDuration);
    }
    IsPassThrough(): boolean {
        return (this.m_LingerDuration < 0);
    }
    GetTransitionDuration(): number {
        return this.m_TransitionDuration;
    }
    GetTileOpacities(): Dictionary<number> {
        return this.m_TileOpacities;
    }
	GetNumHiddenTiles(): number {
		var numHiddenTiles = 0;
        var key:string;

        for (key in this.m_TileOpacities)
            if (this.m_TileOpacities[key] == 0)
                numHiddenTiles++;

		return numHiddenTiles;
	}
};

//////////////////////// CameraAnimation //////////////////////////////////////////////////////////////

class CameraAnimation {
    private m_isPausable : boolean;
    private m_isScrubbable: boolean;
    private m_loopFrame : CameraAnimationKeyframe;
    private m_frames : CameraAnimationKeyframe[];
    constructor(isPausable: boolean, isScrubbable: boolean) {
        this.m_isPausable = isPausable;
        this.m_isScrubbable = isScrubbable;
        this.m_loopFrame = new CameraAnimationKeyframe(new Quaternion, new Vector3, -1, 0, 0, {});
        this.m_frames = [];
    }
    dispose() {
        this.m_loopFrame = null;
        for (let frame of this.m_frames)
            frame.dispose();
        this.m_frames = [];
    }
    GetNumFrames(bIsLooping: boolean): number {
        if (this.m_frames.length > 1)
            return this.m_frames.length + (bIsLooping ? 1 : 0);
        else
            return this.m_frames.length;
    }
    GetLastFrameTime(bIsLooping: boolean): number {
        if (this.m_frames.length > 0) {
            return this.LastFrame(bIsLooping).GetStartElapsedTime();
        } else {
            return 0.0;
        }
    }
    Clear() {
        this.m_frames = [];
    }
    // Returns last frame if not looping, or first frame (i.e., last frame of one cycle) if looping
    LastFrame(bIsLooping: boolean): CameraAnimationKeyframe {
        return this.GetFrame(this.GetNumFrames(bIsLooping) - 1);
    }
    GetFrame(i: number): CameraAnimationKeyframe {
        // Allow retrieve past end-of-container, to allow looping mechanism
        if (i < this.m_frames.length)
            return this.m_frames[i];
        //Assert(i == this.m_frames.length);
        var lastIndex = this.m_frames.length-1;
        this.m_loopFrame = new CameraAnimationKeyframe(this.m_frames[0].GetRBT().rotation, this.m_frames[0].GetRBT().translation,
            this.m_frames[lastIndex].GetStartElapsedTime() + this.m_frames[lastIndex].GetLingerDuration() + this.m_frames[lastIndex].GetTransitionDuration(), 
            this.m_frames[0].GetLingerDuration(true), this.m_frames[0].GetTransitionDuration(), this.m_frames[0].GetTileOpacities());
        return this.m_loopFrame;
    }
    PushFrame(rotation: Quaternion, translation: Vector3, startElapsedTime: number, lingerDuration: number, transitionDuration: number, tileOpacities: Dictionary<number>) {
        this.m_frames.push(new CameraAnimationKeyframe(rotation, translation, startElapsedTime, lingerDuration, transitionDuration, tileOpacities));
    }
    IsPausable(): boolean {
        return this.m_isPausable;
    }
    IsScrubbable(): boolean {
        return this.m_isScrubbable;
    }
    GetTangent(index: number, isPassThrough: boolean, isStartPoint: boolean): Vector3
    {
        // assumes there are at least 2 points
        var numPoints = this.m_frames.length;
        var nextIndex = index + 1;
        if (nextIndex >= numPoints)
            nextIndex = 0;
        var prevIndex = index - 1;
        if (prevIndex < 0)
            prevIndex = numPoints-1;
    
        if (isPassThrough)
            return new Vector3().subVectors(this.m_frames[nextIndex].GetRBT().translation, this.m_frames[prevIndex].GetRBT().translation).multiplyScalar(0.5);
        else if (isStartPoint)
            return new Vector3().subVectors(this.m_frames[nextIndex].GetRBT().translation, this.m_frames[index].GetRBT().translation).multiplyScalar(0.5);
        else
            // is end point
            return new Vector3().subVectors(this.m_frames[index].GetRBT().translation, this.m_frames[prevIndex].GetRBT().translation).multiplyScalar(0.5);
    }
    InterpolatePosition(firstIndex: number, t: number, firstPointIsPassThrough: boolean, nextPointIsPassThrough: boolean): Vector3
    {
        //Assert( firstIndex < m_frames.size() );
        //Assert( m_frames.size() >= 1 );

        var nextIndex = firstIndex+1;
        if ( nextIndex >= this.m_frames.length )
            nextIndex = 0;

        var p0 = this.m_frames[firstIndex].GetRBT().translation;
        var p1 = this.m_frames[nextIndex].GetRBT().translation;

        if ( t <= 0.0 || this.m_frames.length == 1 )
            return p0;
        if ( t >= 1.0 )
            return p1;

        if (!firstPointIsPassThrough && !nextPointIsPassThrough)
            // standard lerp
            return new Vector3().lerpVectors(p0, p1, t);

        var Tan0 = this.GetTangent(firstIndex, firstPointIsPassThrough, true);
        var Tan1 = this.GetTangent(nextIndex, nextPointIsPassThrough, false);

        // Simplified version of the above.
        var m00 = 2.0*p0.x - 2.0*p1.x + Tan0.x + Tan1.x;
        var m01 = 2.0*p0.y - 2.0*p1.y + Tan0.y + Tan1.y;
        var m02 = 2.0*p0.z - 2.0*p1.z + Tan0.z + Tan1.z;

        var m10 = -3.0*p0.x + 3.0*p1.x - 2.0*Tan0.x - Tan1.x;
        var m11 = -3.0*p0.y + 3.0*p1.y - 2.0*Tan0.y - Tan1.y;
        var m12 = -3.0*p0.z + 3.0*p1.z - 2.0*Tan0.z - Tan1.z;

        var t2 = t*t;
        var t3 = t2*t;

        return new Vector3(
            m00*t3 + m10*t2 + Tan0.x*t + p0.x,
            m01*t3 + m11*t2 + Tan0.y*t + p0.y,
            m02*t3 + m12*t2 + Tan0.z*t + p0.z );
    }
};

//////////////////////// CameraAnimator //////////////////////////////////////////////////////////////

export class CameraAnimator {
    private m_animationState : AnimationStateEnum = AnimationStateEnum.AS_COMPLETED;
    private m_animationUI : CameraAnimation  = new CameraAnimation(false, false);
    m_animationBookmarks : CameraAnimation  = new CameraAnimation(true, true);
    private m_pActiveAnimation : CameraAnimation = this.m_animationUI;
    
    private m_CurrentElapsedTime : number = 0.0;
    private m_CurrentSplinePos : number = 0.0;
    private m_isOnBookmarkPath : boolean = false;
    private m_easingType = EasingType.ET_QuadraticInOut;	
    private m_bookmarkSplinePosAfterAnimation : number = 0;
    private m_bookmarkElapsedTimeAfterAnimation : number = 0;
    private m_bookmarkPlayAfterAnimation : boolean = false;
    private m_bookmarkLooping : boolean = false;

    constructor() {
    }
    dispose() {
        this.m_animationBookmarks.dispose();
        this.m_animationUI.dispose();
    }
    SetOnBookmarkPath(isOnBookmarkPath: boolean) {
        this.m_isOnBookmarkPath = isOnBookmarkPath;
    }
    // Returns true if we're animating through bookmarks.  State may still be paused, completed, etc...
    IsAnimatingBookmarks(): boolean {
        return this.m_pActiveAnimation == this.m_animationBookmarks;
    }
    IsAnimating(): boolean {
        return this.m_animationState == AnimationStateEnum.AS_ANIMATING;
    }
    IsAnimationCompleted(): boolean {
        return this.m_animationState == AnimationStateEnum.AS_COMPLETED;
    }
    CheckSetAnimationCompleted() {
        if ((this.m_animationState == AnimationStateEnum.AS_PAUSED) && this.IsAnimatingBookmarks() && !this.IsLooping() && this.IsOnBookmarkPath() && (this.m_animationBookmarks.GetNumFrames(false) > 0)) {
            // paused AND animating bookmarks AND not looping AND is on bookmark path AND there is at least one bookmark frame
            var lastFrame = this.m_animationBookmarks.GetFrame(this.m_animationBookmarks.GetNumFrames(false)-1);
            if (this.m_CurrentElapsedTime >= lastFrame.GetStartElapsedTime()+lastFrame.GetLingerDuration())
                this.m_animationState = AnimationStateEnum.AS_COMPLETED;
        }
    }
    IsOnBookmarkPath(): boolean {
        return this.m_isOnBookmarkPath;
    }
    SetBookmarkLooping(bLooping: boolean) {
        this.m_bookmarkLooping = bLooping;
    }
    SetBookmarkPlayAfterAnimation(bookmarkPlayAfterAnimation: boolean) {
        this.m_bookmarkPlayAfterAnimation = bookmarkPlayAfterAnimation;
    }
    GetBookmarkSplinePosAfterAnimation(): number {
        return this.m_bookmarkSplinePosAfterAnimation;
    }
    GetBookmarkElapsedTimeAfterAnimation(): number {
        return this.m_bookmarkElapsedTimeAfterAnimation;
    }
    IsLooping(): boolean {
        // Are we both a) animating bookmarks now; and b) have loop flag enabled.
        return this.m_pActiveAnimation == this.m_animationBookmarks && this.m_bookmarkLooping;
    }
    GetElapsedTime(): number {
        return this.m_CurrentElapsedTime;
    }    
    GetElapsedTimeNormalized(): number {
        var endT = this.GetAnimationEndTime(this.m_pActiveAnimation, this.IsLooping());
		if (endT == 0)
			return 0;
		else
			return this.m_CurrentElapsedTime / endT;
    }    
    GetSplinePos(): number {
        return this.IsAnimatingBookmarks() ? this.m_CurrentSplinePos : this.m_bookmarkSplinePosAfterAnimation;
    }
    GetPrevBookmarkIndex(): number {
        var index = Math.ceil(this.GetSplinePos());
        if (index >= 0) {
            index = Math.round(index - 1);
            if (index < 0)
                index = this.m_animationBookmarks.GetNumFrames(false) - 1;
        }
        else
            index = this.m_animationBookmarks.GetNumFrames(false) - 1;
        return index;
    }
    GetNextBookmarkIndex(): number {
        var index = Math.floor(this.GetSplinePos());
        if (index >= 0)
            index = Math.round(index + 1);
        else
            index = 1;
        if (index >= this.m_animationBookmarks.GetNumFrames(false))
            index = 0;
        return index;
    }
    GetClosestBookmarkIndexFromSplinePos(splinePos: number): number {
        var index = Math.round(splinePos);
        if ((index < 0) || (index >= this.m_animationBookmarks.GetNumFrames(false)))
            index = 0;
        return index;
    }
    GetStageIdBookmarkIndex(): number {
        var splinePos = this.GetSplinePos();
        return this.GetClosestBookmarkIndexFromSplinePos(splinePos);
    }
    SetRBTFromElapsedTime(animation: CameraAnimation, elapsedTime: number, isLooping: boolean, rotation: Quaternion, translation: Vector3, tileOpacities: Dictionary<number>): number {
        var nframes = animation.GetNumFrames(isLooping);

        // don't bother if there are 0 frames
        if (nframes <= 0) return 0;

        // figure out what frame is the destination
        var idstframe = 0;
        for (idstframe = 1; idstframe < nframes-1; idstframe++) {
            if (elapsedTime < animation.GetFrame(idstframe).GetStartElapsedTime()) {
                break;
            }
        }
        idstframe = _Math.clamp(idstframe, 0, nframes-1);
        var isrcframe = idstframe - 1;
        isrcframe = _Math.clamp(isrcframe, 0, nframes-1);
        
        // note: if we are looping, then the last frame is the same as the first, so we need to skip over the duplicate when getting the before and after frames
        var iBeforeSrcFrame = (isrcframe > 0) ? isrcframe - 1 : ((isLooping) ? nframes-2 : nframes-1);
        var iAfterDstFrame = (idstframe < nframes-1) ? idstframe + 1 : ((isLooping) ? 1 : 0);

        var beforeSrcFrame = animation.GetFrame(iBeforeSrcFrame);
        var srcframe = animation.GetFrame(isrcframe);
        var dstframe = animation.GetFrame(idstframe);
        var afterDstFrame = animation.GetFrame(iAfterDstFrame);

        // t_01 = current keyframe segement, normalized between 0.0 and 1.0
        var t_01 = 0.0;
        if (elapsedTime > srcframe.GetStartElapsedTime() + srcframe.GetLingerDuration())
            t_01 = (elapsedTime - srcframe.GetStartElapsedTime() - srcframe.GetLingerDuration()) / srcframe.GetTransitionDuration();
        // if we are at the last bookmark, and there is a linger time, and we are not looping, t_01 will be greater than 1. We want to clamp it
        t_01 = _Math.clamp(t_01, 0, 1);

        // if the key frame before or after a particular key frame is the same RBT, then we can't do a pass-through, or else we'll get a spline looping behavior
        // between the matching key frames
        var srcFrameIsPassThrough = srcframe.IsPassThrough() && (isrcframe > 0 || isLooping) &&
                                    !_Math.equalRBTs(beforeSrcFrame.GetRBT(), srcframe.GetRBT()) && !_Math.equalRBTs(dstframe.GetRBT(), srcframe.GetRBT());
        var dstFrameIsPassThrough = dstframe.IsPassThrough() && (idstframe <  nframes-1 || isLooping) &&
                                    !_Math.equalRBTs(srcframe.GetRBT(), dstframe.GetRBT()) && !_Math.equalRBTs(afterDstFrame.GetRBT(), dstframe.GetRBT());

        var eased_t_01 = t_01;
        var non_pass_through_eased_t_01 = EasingFunctions.easeInOutQuad(t_01);
        if (srcFrameIsPassThrough || dstFrameIsPassThrough) {
            // src or dst is a passthrough frame
            var prevDuration = (srcFrameIsPassThrough) ? beforeSrcFrame.GetTransitionDuration() : 0;
            var curDuration = srcframe.GetTransitionDuration();
            var nextDuration = (dstFrameIsPassThrough) ? dstframe.GetTransitionDuration() : 0;

            eased_t_01 = EasingFunctions.ApplyPassThroughEasing(t_01, prevDuration, curDuration, nextDuration);
        } else {
            // we only use quadratic easing, so we don't really need to keep track of the type for now
            //eased_t_01 = ApplyEasing(t_01, this.m_easingType);
            eased_t_01 = non_pass_through_eased_t_01;
        }

        var srcOpacities:Dictionary<number> = srcframe.GetTileOpacities();
        var dstOpacities:Dictionary<number> = dstframe.GetTileOpacities();
        var key:string;
        for (key in srcOpacities)
            tileOpacities[key] = _Math.lerp(srcOpacities[key], dstOpacities[key], eased_t_01);

        var splinePos:number = isrcframe + eased_t_01;

        // position interpolation (this will either lerp or do spline interpolation)
        translation.copy(animation.InterpolatePosition(isrcframe, eased_t_01, srcFrameIsPassThrough, dstFrameIsPassThrough));

        // note: we always want to apply non-pass-through easing for rotation
        // if we have a pass-through situation, the position will be spline interpolated, but the rotation will still be lerped.
        // this should still look nice and smooth, like a baby's bottom
        _Math.lerpQuats(srcframe.GetRBT().rotation, dstframe.GetRBT().rotation, rotation, non_pass_through_eased_t_01);

        // spline interpolation of the quaterions is still not quite working (i think there might be a shortest path problem), so we will do linear interpolation for now
        // update: we don't want to do spline interpolation of quats anyway. There is a better way to move the camera (see above note)
        //camera->SetOrientation(animation->InterpolateQuaternion(isrcframe, eased_t_01));

        return splinePos;
    }
    GetAnimationEndTime(animation: CameraAnimation, isLooping: boolean): number {
        // if the animation is looping, then the linger and transition duration of the last bookmark will included in GetLastFrameTime()
        // if it isn't looping, then we have to add in the linger duration of the last bookmark
        var endT = animation.GetLastFrameTime(isLooping);
        if ((!isLooping) && (animation.GetNumFrames(false) > 0))
            endT += animation.LastFrame(false).GetLingerDuration();
        
        return endT;
    }
    // Added for NYT.
    setAnimationScrubT(camera: Camera, tileOpacities: Dictionary<number>, t: number) {
        // Lines below are required to 'init' the animation to the bookmark path.
        this.m_pActiveAnimation = this.m_animationBookmarks;
        this.m_isOnBookmarkPath = true;

        // Perform the scrub.
        t = _Math.clamp(t, 0, 1);
        var endT = this.GetAnimationEndTime(this.m_pActiveAnimation, this.IsLooping());
        this.m_CurrentElapsedTime = t * endT;
        this.m_CurrentSplinePos = this.SetRBTFromElapsedTime(this.m_pActiveAnimation, this.m_CurrentElapsedTime, this.IsLooping(),
            camera.quaternion, camera.position, tileOpacities);
    }
    AnimateCamera(camera: Camera, tileOpacities: Dictionary<number>, dt: number): boolean {
        // note dt can be negative (for scrubbing)
        
        //Clamp( dt, 0.001f, 0.033f );
        this.m_CurrentElapsedTime += dt;

        var endT = this.GetAnimationEndTime(this.m_pActiveAnimation, this.IsLooping());
        var isAnimationComplete = (this.m_CurrentElapsedTime >= endT);
        this.m_CurrentElapsedTime = _Math.clamp(this.m_CurrentElapsedTime, 0, endT);
        this.m_CurrentSplinePos = this.SetRBTFromElapsedTime(this.m_pActiveAnimation, this.m_CurrentElapsedTime, this.IsLooping(), 
            camera.quaternion, camera.position, tileOpacities);

        return isAnimationComplete;
    }
    AnimateStep(camera: Camera, tileOpacities: Dictionary<number>, dt: number): boolean {

        var isAnimationComplete : boolean = this.AnimateCamera(camera, tileOpacities, dt);

        if (isAnimationComplete) {
            
            /*
            if (m_SimpleScene.IsInNavMode())
                m_SimpleScene.SetNavmodeCenter();
            */
            this.m_animationState = AnimationStateEnum.AS_COMPLETED;

            // If we're animating through the bookmarks...
            if (this.m_pActiveAnimation == this.m_animationBookmarks) {
                // Test the looping flag *now*, in case the user has turned looping on/off during playback.
                if (this.m_bookmarkLooping && this.m_animationBookmarks.GetNumFrames(false) > 1) {
                    // looping, so start the animation over
                    this.m_bookmarkSplinePosAfterAnimation = 0.0;
                    this.m_bookmarkElapsedTimeAfterAnimation = 0.0;
                    this.m_bookmarkPlayAfterAnimation = true;
                } else {
                    // done. shut it down
                    this.m_bookmarkSplinePosAfterAnimation = -1.0;
                    this.m_bookmarkElapsedTimeAfterAnimation = -1.0;
                    this.m_bookmarkPlayAfterAnimation = false;
                }
            }

            if (this.m_bookmarkSplinePosAfterAnimation >= 0.0) {
                this.m_CurrentSplinePos = this.m_bookmarkSplinePosAfterAnimation;
                if (this.m_bookmarkElapsedTimeAfterAnimation >= 0) {
                    this.m_CurrentElapsedTime = this.m_bookmarkElapsedTimeAfterAnimation;
                } else {
                    // calculate it from this.m_bookmarkSplinePosAfterAnimation
                    var frameIndex = Math.floor(this.m_bookmarkSplinePosAfterAnimation);
                    var frame = this.m_animationBookmarks.GetFrame(frameIndex);
                    // if we only have m_CurrentSplinePos, then we have no way of knowing how far into linger time we are
                    // so just start it over
                    this.m_CurrentElapsedTime = frame.GetStartElapsedTime();

                    // add in whatever amount we have moved in transition
                    var additionalElapsedTime = (this.m_bookmarkSplinePosAfterAnimation - frameIndex)*frame.GetTransitionDuration();
                    if (additionalElapsedTime > 0)
                        this.m_CurrentElapsedTime += frame.GetLingerDuration() + additionalElapsedTime;
                }

                this.m_pActiveAnimation = this.m_animationBookmarks;
                this.m_isOnBookmarkPath = true;
                this.m_animationState = AnimationStateEnum.AS_PAUSED;
                if (this.m_bookmarkPlayAfterAnimation)
                    this.m_animationState = AnimationStateEnum.AS_ANIMATING;
            }
            this.m_bookmarkPlayAfterAnimation = false;
        }

        return this.IsAnimating();
    }
    GetBookmarkPathSplinePos(): number {
        return this.IsAnimatingBookmarks() ? this.m_CurrentSplinePos : this.m_bookmarkSplinePosAfterAnimation;
    }
// creates a key/opacity dictionary from tile opacities
    GetCurrentTileAnimationOpacities(tiles: {[key: string]: Material}): Dictionary<number>
    {
        var opacities: Dictionary<number> = {};
        for (var key in tiles) {
            if (tiles.hasOwnProperty(key)) {
                opacities[key] = tiles[key].opacity;
            }
        }
        return opacities;
    }
    // creates a key/opacity dictionary from hidden tile names array
    GetTileAnimationOpacitiesFromHiddenTileNames(tiles: {[key: string]: Material}, hiddenTileNames: string[]): Dictionary<number>
    {
        var opacities : Dictionary<number> = {};
        for (var key in tiles) {
            if (tiles.hasOwnProperty(key))
                opacities[key] = (hiddenTileNames.indexOf(key) > -1) ? 0 : 1;
                // Array.includes isn't available in ES2015 - only ES2016 and later.
                //(hiddenTileNames.includes(key)) ? 0 : 1;
        }
        return opacities;
    }
    SetupAnimation_UIFromCameraToBookmark(tiles: {[key: string]: Material}, linkVizMode: boolean, camera: Camera, bookmarkIndex: number, playBookmarkAnimationOnComplete:boolean=false, CameraDurationSecs:number=0.5) {
        var src = new RBT(camera.quaternion, camera.position);
        var srcTileOpacities = this.GetCurrentTileAnimationOpacities(tiles);
        var dst = this.m_animationBookmarks.GetFrame(bookmarkIndex).GetRBT();
        var dstTileOpacities = (linkVizMode) ? this.m_animationBookmarks.GetFrame(bookmarkIndex).GetTileOpacities() : srcTileOpacities;

        if (_Math.equalRBTsAndTileOpacities(src, dst, srcTileOpacities, dstTileOpacities))
            // we are already at the right spot and the opacities are the same
            CameraDurationSecs = 0;

        this.SetupAnimation_UI(src, dst, srcTileOpacities, dstTileOpacities, CameraDurationSecs, bookmarkIndex, -1, playBookmarkAnimationOnComplete);
    }
    SetupAnimation_UIFromCameraToCamera(tiles: {[key: string]: Material}, camera: Camera, toCamera: Camera) {
        var src = new RBT(camera.quaternion, camera.position);
        var dst = new RBT(toCamera.quaternion, toCamera.position);
        var tileOpacities = this.GetCurrentTileAnimationOpacities(tiles);
        
        var CameraDurationSecs = 0.5;
        if (_Math.equalRBTsAndTileOpacities(src, dst, tileOpacities, tileOpacities))
            // we are already at the right spot and the opacities are the same
            CameraDurationSecs = 0;

        this.SetupAnimation_UI(src, dst, tileOpacities, tileOpacities, CameraDurationSecs, 0, -1, false);
    }

    GetCanvasOpacitiesFromCameraPosition(camera: Camera, tileOpacities: Dictionary<number>) {
	
        var nframes = this.m_animationBookmarks.GetNumFrames(false);
    
        if (nframes > 0) {
    
            // find the closest bookmark point to camera
            var minDistIndex = -1;
            var minDist = 0;
            var minDistNumHiddenTiles = -1;
    
            for (var i = 0; i < nframes; i++) {
                var frame = this.m_animationBookmarks.GetFrame(i);
                var pos = frame.GetRBT().translation;
                var dist = pos.distanceTo(camera.position);
    
                if (minDistIndex < 0 || dist < minDist) {
                    // this bookmark position is closer
                    minDistIndex = i;
                    minDist = dist;
                    minDistNumHiddenTiles = -1;
                } else if (dist == minDist) {
                    // if the distances are the same, take the bookmark with more visible canvases
                    var numHiddenTiles = frame.GetNumHiddenTiles();
                    if (minDistNumHiddenTiles < 0)
                        minDistNumHiddenTiles = this.m_animationBookmarks.GetFrame(minDistIndex).GetNumHiddenTiles();
                    if (numHiddenTiles < minDistNumHiddenTiles) {
                        minDistIndex = i;
                        minDistNumHiddenTiles = numHiddenTiles;
                    }
                }
            }
    
            // now, we just want to get the canvas opacities at minDistIndex. We don't need to set the camera position and orientation, so use a temp cam
            var quat = new Quaternion;
            var pos = new Vector3;
            var frame = this.m_animationBookmarks.GetFrame(minDistIndex);
            this.SetRBTFromElapsedTime(this.m_animationBookmarks, frame.GetStartElapsedTime(), this.m_bookmarkLooping, quat, pos, tileOpacities);
        }
    }

    getBookmarkClosestToCameraPoint(camera: Camera) {
        var nframes = this.m_animationBookmarks.GetNumFrames(false);
    
        if (nframes > 0) {
            // find the closest bookmark point to camera
            var minDistIndex = -1;
            var minDist = 0;
            var minDistNumHiddenTiles = -1;
    
            for (var i = 0; i < nframes; i++) {
                var frame = this.m_animationBookmarks.GetFrame(i);
                var pos = frame.GetRBT().translation;
                var dist = pos.distanceTo(camera.position);
    
                if (minDistIndex < 0 || dist < minDist) {
                    // this bookmark position is closer
                    minDistIndex = i;
                    minDist = dist;
                    minDistNumHiddenTiles = -1;
                } 
            }
            return minDistIndex;
        }     
    }
    
    CheckSetupAnimation_UIFromCameraBackToPath(tiles: {[key: string]: Material}, linkVizMode: boolean, camera: Camera, playBookmarkAnimationOnComplete: boolean=false, CameraDurationSecs: number=0.5) {
        var src = new RBT(camera.quaternion, camera.position);
        var srcTileOpacities = this.GetCurrentTileAnimationOpacities(tiles);
        var quat = new Quaternion;
        var pos = new Vector3;
        var tileOpacities = {};
        if (this.m_CurrentElapsedTime < 0)
            // have to go to a valid point on the timeline
            this.m_CurrentElapsedTime = 0;
        this.m_CurrentSplinePos = this.SetRBTFromElapsedTime(this.m_animationBookmarks, this.m_CurrentElapsedTime, this.m_bookmarkLooping, quat, pos, tileOpacities);
        var dstTileOpacities = (linkVizMode) ? tileOpacities : srcTileOpacities;
        var dst = new RBT(quat, pos);

        if (!_Math.equalRBTsAndTileOpacities(src, dst, srcTileOpacities, dstTileOpacities))
            // something is not the same
            this.SetupAnimation_UI(src, dst, srcTileOpacities, dstTileOpacities, CameraDurationSecs, this.m_CurrentSplinePos, this.m_CurrentElapsedTime, playBookmarkAnimationOnComplete);
        else
            // we are on the bookmark path. make sure the active animation is set
            this.m_pActiveAnimation = this.m_animationBookmarks;
    }

    SetupAnimation_UI(src:RBT, dst:RBT, srcTileOpacities: Dictionary<number>, dstTileOpacities: Dictionary<number>,
        CameraDurationSecs: number, bookmarkSplinePosAfterAnimation: number, bookmarkElapsedTimeAfterAnimation: number,
        playBookmarkAnimationOnComplete: boolean, easing:EasingType=EasingType.ET_QuadraticInOut)
    {
        this.m_animationUI.Clear();
        this.m_animationUI.PushFrame(src.rotation, src.translation, 0.0, 0, CameraDurationSecs, srcTileOpacities);
        this.m_animationUI.PushFrame(dst.rotation, dst.translation, CameraDurationSecs, 0.0, 0.0, dstTileOpacities);
        this.m_pActiveAnimation = this.m_animationUI;

        this.m_easingType = easing;
        this.m_CurrentElapsedTime = 0.0;
        this.m_CurrentSplinePos = 0.0;
        this.m_bookmarkSplinePosAfterAnimation = bookmarkSplinePosAfterAnimation;
        this.m_bookmarkElapsedTimeAfterAnimation = bookmarkElapsedTimeAfterAnimation;
        this.m_bookmarkPlayAfterAnimation = playBookmarkAnimationOnComplete;
        this.m_animationState = AnimationStateEnum.AS_ANIMATING;
    }
    ClearAnimation()
    {
        this.m_animationUI.Clear();
        this.m_animationBookmarks.Clear();
        this.m_pActiveAnimation = this.m_animationUI;
        this.m_animationState = AnimationStateEnum.AS_COMPLETED;
    }
    ScrubDelta(camera: Camera, tileOpacities: Dictionary<number>, tScrubDelta: number) {
        // only allow scrub of bookmarks
        if (this.m_pActiveAnimation != this.m_animationBookmarks) {
            // bookmarks isn't active
            if (this.m_animationState == AnimationStateEnum.AS_COMPLETED || this.m_animationState == AnimationStateEnum.AS_PAUSED) {
                // but its completed, so activate the bookmarks
                this.m_CurrentElapsedTime = 0;
                this.m_pActiveAnimation = this.m_animationBookmarks;
                this.m_isOnBookmarkPath = true;
            }
        } 

        // only allow bookmark scrub if paused or completed
        if (this.m_pActiveAnimation == this.m_animationBookmarks) {
            if (this.m_animationState == AnimationStateEnum.AS_COMPLETED || this.m_animationState == AnimationStateEnum.AS_PAUSED) {
                // adjust the time
                this.AnimateCamera(camera, tileOpacities, tScrubDelta);
                this.m_animationState = AnimationStateEnum.AS_PAUSED;
                //m_SimpleScene->RequestRedraw();
            }
        }
    }
    GetInterpolatedRBT(src: RBT, dst: RBT, t01: number): RBT {
        var result = new RBT(new Quaternion, new Vector3);
        //var t = ApplyEasing(t01, ET_QuadraticInOut);
        var t = t01; // easing doesn't feel right
        var SrcPos = src.translation;
        var DstPos = dst.translation;

        result.translation.lerpVectors(SrcPos, DstPos, t);
        _Math.lerpQuats(src.rotation, dst.rotation, result.rotation, t);

        return result;
    }
    SetupAnimation_Bookmarks(tiles: {[key: string]: Material}, bookmarks: Bookmark[], bStartPlaying: boolean, easing:EasingType=EasingType.ET_QuadraticInOut) {
        
        var bookmarksLength = (bookmarks != null) ? bookmarks.length : this.m_animationBookmarks.GetNumFrames(false);
        if (bookmarks != null)
            this.m_animationBookmarks.Clear();

        var tileOpacities : Dictionary<number>;
        if (bookmarksLength == 1) {
            if (bookmarks != null) {
                tileOpacities = this.GetTileAnimationOpacitiesFromHiddenTileNames(tiles, bookmarks[0].hiddenTileNames);
                this.m_animationBookmarks.PushFrame(bookmarks[0].quat, bookmarks[0].vec, 0.0, 0.0, 0.0, tileOpacities);
            }
        } else if (bookmarksLength > 1) {
            if (bookmarks != null) {
                var startElapsedTime = 0;
                for (var i = 0; i < bookmarks.length; i++) {
                    tileOpacities = this.GetTileAnimationOpacitiesFromHiddenTileNames(tiles, bookmarks[i].hiddenTileNames);
                    this.m_animationBookmarks.PushFrame(bookmarks[i].quat, bookmarks[i].vec, startElapsedTime, bookmarks[i].linger, bookmarks[i].transition, tileOpacities);
                    // note: linger duration can be negative here (for pass through), but we don't want to include that in elapsed time
                    startElapsedTime += Math.max(bookmarks[i].linger, 0) + bookmarks[i].transition;
                }
            }
        }

        // if we are in the middle of a UI animation, we don't want to interrupt it
        if (!this.IsAnimating() || this.IsAnimatingBookmarks()) {
            // start off not on the timeline
            this.m_CurrentElapsedTime = -1.0;
            this.m_CurrentSplinePos = -1.0;
            this.m_easingType = easing;
            if (bookmarksLength == 0)
                this.m_animationState = AnimationStateEnum.AS_COMPLETED;
            else if (bStartPlaying)
                this.m_animationState = AnimationStateEnum.AS_ANIMATING;
            else
                this.m_animationState = AnimationStateEnum.AS_PAUSED;
            if (bStartPlaying)
                this.m_pActiveAnimation = this.m_animationBookmarks;
        }

        this.m_bookmarkSplinePosAfterAnimation = -1.0;
        this.m_bookmarkElapsedTimeAfterAnimation = -1.0;
        this.m_bookmarkPlayAfterAnimation = false;

        // this clears elapsed time on the UI side and not select any bookmark
        //m_SimpleScene->NotifyUI_UpdateElapsedTime(-1);
    }
    PauseAnimation()
    {
        if (this.m_animationState == AnimationStateEnum.AS_ANIMATING) {
            this.m_animationState = AnimationStateEnum.AS_PAUSED;
            if (this.m_pActiveAnimation == this.m_animationBookmarks) {
                this.m_isOnBookmarkPath = true;
            } else {
                // set this up so that we can animate to the right spot when we resume
                this.m_isOnBookmarkPath = false;
                this.m_bookmarkPlayAfterAnimation = false;
                this.m_CurrentSplinePos = this.m_bookmarkSplinePosAfterAnimation;
                this.m_CurrentElapsedTime = this.m_bookmarkElapsedTimeAfterAnimation;
            }
        }
    }
    ResumeAnimation(tiles: {[key: string]: Material}, linkVizMode: boolean, camera: Camera) {
        if (this.m_animationState == AnimationStateEnum.AS_PAUSED) {
            this.m_animationState = AnimationStateEnum.AS_ANIMATING;

            this.CheckSetupAnimation_UIFromCameraBackToPath(tiles, linkVizMode, camera, true);
        }
    }
    IsAnimationResumable(): boolean {
        return this.m_animationState == AnimationStateEnum.AS_PAUSED;
    }
    IsAnimationPausable(): boolean {
        return this.m_animationState == AnimationStateEnum.AS_ANIMATING && this.m_pActiveAnimation.IsPausable();
    }
};
